import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MobileService } from '@klickdata/core/mobile';
import { Filter, GlobalFilterProperty, TableFilterComponent, TableSource } from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { BehaviorSubject, Observable, map, shareReplay, takeUntil } from 'rxjs';
import { UserListService } from './user-list.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';

@Component({
    selector: 'app-user-table-base',
    templateUrl: './user-table-base.component.html',
    styleUrls: ['./user-table-base.component.scss'],
    providers: [UserListService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class UserTableBaseComponent extends OnDestroyHandler implements OnInit {
    @Input() dataSource = new TableSource<User>();
    @Input() matSortDirection: 'asc' | 'desc' | '' = 'desc';
    @Input() matSortActive = 'title';
    @Input() columnsData: { [key: string]: TemplateRef<any> };
    @Input() otherContext: { [key: string]: TemplateRef<any> };
    @Input() columnsKeys: any;
    @Input() tableFilterItems: Filter<any>[];
    @Input() activeFilter: GlobalFilterProperty;
    @Input() expandingTable: boolean;
    @Input() expandedFn: (user: User) => boolean = (user: User) => this.expandedRowId === user.id;
    @Input() usersCountToHideFiltersAndPaginator: number;
    @Output() expansionToggled = new EventEmitter<User>();
    @Output() onRowClick = new EventEmitter<User>();
    public showSelectionActions: boolean;
    @Input() canSelect: boolean;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @Input() filter: TableFilterComponent;
    @Input() expandedRowId: number | null;
    @Input() pageSizeOptions = [25, 50, 100];
    @Input() filtersExpanded = false;
    GlobalFilterProperty = GlobalFilterProperty;
    public isMobile$: Observable<boolean>;
    selection = new SelectionModel<User>(true, []);
    totalUsersCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    constructor(
        protected userListService: UserListService,
        protected mobile: MobileService,
        protected cdr: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.dataSource.service = this.userListService;
        this.isMobile$ = this.mobile.isMobile().pipe(shareReplay());
        this.selection.changed.pipe(map((selection) => selection.source.selected)).subscribe((selected) => {
            this.showSelectionActions = selected.length > 1;
            this.cdr.markForCheck();
        });
        this.dataSource.latestData
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.totalUsersCount$.next(this.dataSource.length));
    }

    public get columns(): string[] {
        let columns = [];
        if (!!this.columnsData) {
            columns = Object.keys(this.columnsData);
        }
        if (this.canSelect) {
            columns.unshift('select');
        }
        return columns;
    }
    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filter = this.filter;
        if (this.activeFilter) {
            this.filter.setActiveFilter(this.activeFilter);
        }
        this.updateFilters();
        setTimeout(() => {
            this.filtersExpanded = this.filter.hasActiveFilters();
            this.cdr.markForCheck();
        }, 1000);
    }
    private updateFilters() {
        const appliedFilters = [];
        if (this.tableFilterItems) {
            this.tableFilterItems.forEach((filterItem) => {
                appliedFilters.push(new Filter(filterItem.property, filterItem.items));
            });
        }

        this.filter.createOrUpdateFilter(appliedFilters);
    }
    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.data.forEach((row) => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: User): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
    }

    toggleExpand(user: User) {
        if (this.expansionToggled.observed) {
            this.expansionToggled.emit(user);
        } else {
            this.expandedRowId = this.expandedRowId === user.id ? null : user.id;
        }
    }
}
